import React, { useCallback, useEffect, useRef, useState } from "react";
import { AlertCircle, Loader2 } from "lucide-react";
import {
  requestSettingsSnapshot,
  sendSettingsSet,
  subscribeSettingsSnapshots,
  subscribeDevicePresence,
} from "../../services/settingsChannel";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Slider } from "../ui/slider";
import { Switch } from "../ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

// Streaming slider writes are throttled to stay under the contract's
// ≤10 sets/s (KhronosFirmware#52); the device applies each one live.
const SET_THROTTLE_MS = 100;

// Sliders suit small bounded ranges (levels, timeouts). A wide range like an
// MQTT port (1–65535) is unusable as a slider, so ints spanning more than this
// fall through to a numeric input instead (issue #53).
const MAX_SLIDER_SPAN = 255;

const APPLY_HINTS = {
  restart_mqtt: "Reconnects the broker when changed",
  needs_reboot: "Takes effect after a reboot",
};

// Firmware labels are written for the device's own LCD roller, which renders
// everything in caps — "15 SEC", "NEVER", "SYD/MEL". Shouted at web scale they
// read as an error, so an all-caps label is recased for display only. Anything
// containing lowercase is left exactly as the firmware sent it, so newer
// firmware can ship properly-cased labels and win outright.
const UNIT_WORDS = {
  SEC: "sec",
  SECS: "secs",
  MIN: "min",
  MINS: "mins",
  HR: "hr",
  HRS: "hrs",
  MS: "ms",
};

// Initialisms that stay upper-case through recasing.
const KEEP_UPPER = new Set(["UTC", "GMT", "AM", "PM", "MQTT", "BLE", "HR"]);

const recaseWord = (word) => {
  if (!word) return word;
  if (KEEP_UPPER.has(word)) return word;
  if (UNIT_WORDS[word]) return UNIT_WORDS[word];
  if (/^\d+$/.test(word)) return word;
  return word.charAt(0) + word.slice(1).toLowerCase();
};

// "15 SEC" → "15 sec", "NEVER" → "Never", "SYD/MEL" → "Syd/Mel", "UTC" → "UTC".
export const humaniseDeviceLabel = (raw) => {
  const text = String(raw ?? "");
  if (!text || /[a-z]/.test(text)) return text;
  return text
    .split(/([\s/–—-]+)/) // keep separators so "SYD/MEL" rebuilds intact
    .map((part) => (/^[\s/–—-]+$/.test(part) ? part : recaseWord(part)))
    .join("");
};

// Enum schema carries `labels` aligned by position with `options` (the option
// value is its index — see KhronosFirmware settings_channel). Show the label so
// dropdowns read as words, not raw integers; fall back to the raw option for
// any label-less enum. Parity is automatic: the labels come straight off the
// firmware, so nothing here is hardcoded or assumed (issue #53).
const optionLabel = (setting, option, index) => {
  const label = Array.isArray(setting.labels) ? setting.labels[index] : undefined;
  return label != null && label !== ""
    ? humaniseDeviceLabel(label)
    : String(option);
};

// An int slider is only sensible when the schema gives a small bounded range.
const isSliderInt = (setting) =>
  setting.type === "int" &&
  Number.isFinite(setting.min) &&
  Number.isFinite(setting.max) &&
  setting.max - setting.min <= MAX_SLIDER_SPAN;

// A section whose fields all share one apply tag shows that tag's hint once at
// the top (issue #53) instead of repeating it on every field; a mixed section
// keeps per-field hints so a header line never over-claims.
const sectionApplyHint = (settings) => {
  const applies = settings.map((s) => s.apply);
  if (applies.some((a) => !a)) return null;
  return new Set(applies).size === 1 ? APPLY_HINTS[applies[0]] || null : null;
};

// "display.brightness" → "Brightness", "clock.format_24h" → "Format 24h",
// "display.timeout_s" → "Timeout (s)". Purely cosmetic — the schema carries no
// per-key label (enum option labels aside), and the keys are the firmware's own
// naming.
const prettyLabel = (key) => {
  const rest = key.includes(".") ? key.slice(key.indexOf(".") + 1) : key;
  const words = rest.split("_").filter(Boolean);
  let suffix = "";
  if (words.length > 1 && words[words.length - 1] === "s") {
    words.pop();
    suffix = " (s)";
  }
  return (
    words.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ") + suffix
  );
};

// Friendly names for keys whose firmware naming reads as jargon. Anything not
// listed falls back to prettyLabel, so new firmware keys still show up.
const KEY_LABELS = {
  "display.auto_bri": "Auto brightness",
  "display.motion_wake": "Wake on motion",
  "clock.format_24h": "24-hour clock",
  "clock.show_temp": "Show temperature",
  "clock.show_hum": "Show humidity",
  "mqtt.enabled": "Send to Home Assistant",
  "mqtt.host": "Broker address",
  "mqtt.port": "Broker port",
  "mqtt.user": "Username",
  "mqtt.password": "Password",
};

export const settingLabel = (key) => KEY_LABELS[key] || prettyLabel(key);

// Section headings by key prefix; unknown prefixes show as-is.
const GROUP_TITLES = {
  display: "Display",
  audio: "Sound",
  clock: "Clock",
  mqtt: "Smart home",
};

// A line under a section heading explaining what it is for.
const GROUP_NOTES = {
  mqtt: "Shows the timer and its room sensors in Home Assistant through your MQTT broker. Home Assistant finds it by itself. The broker must be on your home network.",
};

// Group by the key prefix ("display.brightness" → DISPLAY section).
const groupSettings = (settings) => {
  const groups = new Map();
  for (const s of settings) {
    if (!s || typeof s.key !== "string") continue;
    const dot = s.key.indexOf(".");
    const group = dot > 0 ? s.key.slice(0, dot) : "general";
    if (!groups.has(group)) groups.set(group, []);
    groups.get(group).push(s);
  }
  return [...groups.entries()];
};

// How long to wait for a first snapshot before saying the timer hasn't sent one.
const SNAPSHOT_WAIT_MS = 4000;

/**
 * One timer's own settings (issue #50), opened from its sheet in Settings: a
 * remote editor rendered entirely from the schema the device reports. The
 * device stays the source of truth — every control shows ACKed/snapshot
 * state, never optimistic writes. Firmware that never sends a snapshot
 * (anything predating KhronosFirmware#52) gets a line saying so.
 */
const TimerSettingsPanel = ({ device }) => {
  const [snap, setSnap] = useState(null);
  const [online, setOnline] = useState(undefined);
  const [waited, setWaited] = useState(false);
  const [busyKeys, setBusyKeys] = useState({}); // "devId:key" → true while ack pending
  const [drafts, setDrafts] = useState({}); // "devId:key" → in-progress input/slider value
  const [error, setError] = useState(null);
  // Slider drags stream throttled sets; the draft is only released once the
  // ack for the post-release send lands, so the thumb never snaps mid-drag.
  const throttleRef = useRef({});
  const committedRef = useRef({});
  const deviceId = device.id;

  useEffect(() => {
    requestSettingsSnapshot(deviceId);
    const t = setTimeout(() => setWaited(true), SNAPSHOT_WAIT_MS);
    return () => clearTimeout(t);
  }, [deviceId]);

  useEffect(() => {
    const unsubSnapshots = subscribeSettingsSnapshots((s) => {
      if (s?.device_id !== deviceId || !Array.isArray(s.settings)) return;
      setSnap(s);
      setOnline((prev) => {
        // A cache replay says nothing about *current* presence — only fill
        // the gap when no presence event has spoken yet.
        if (s.cached) return prev === undefined ? false : prev;
        return true;
      });
    });
    const unsubPresence = subscribeDevicePresence((evt) => {
      if (evt?.device_id !== deviceId) return;
      setOnline(evt.online === true);
      if (evt.online) requestSettingsSnapshot(deviceId);
    });
    return () => {
      unsubSnapshots();
      unsubPresence();
    };
  }, [deviceId]);

  useEffect(() => {
    if (!error) return;
    const t = setTimeout(() => setError(null), 8000);
    return () => clearTimeout(t);
  }, [error]);

  const setBusy = (k, busy) =>
    setBusyKeys((prev) => {
      const next = { ...prev };
      if (busy) next[k] = true;
      else delete next[k];
      return next;
    });

  const clearDraft = useCallback((k) => {
    setDrafts((prev) => {
      if (!(k in prev)) return prev;
      const next = { ...prev };
      delete next[k];
      return next;
    });
  }, []);

  // Fold an ack's effective value (post-clamping) into the rendered snapshot.
  // The device also broadcasts a fresh snapshot after every applied change;
  // this just avoids a visible lag between the two.
  const applyAck = useCallback(
    (deviceId, key, ack) => {
      const k = `${deviceId}:${key}`;
      setBusy(k, false);
      if (ack?.value !== undefined) {
        setSnap((prev) =>
          prev
            ? {
                ...prev,
                settings: prev.settings.map((s) =>
                  s.key === key ? { ...s, value: ack.value } : s,
                ),
              }
            : prev,
        );
      }
      const t = throttleRef.current[k];
      const streamStillActive = t && (t.timer || !committedRef.current[k]);
      if (!streamStillActive) {
        clearDraft(k);
        delete committedRef.current[k];
      }
    },
    [clearDraft],
  );

  const failSet = useCallback(
    (deviceId, key, err) => {
      const k = `${deviceId}:${key}`;
      setBusy(k, false);
      delete committedRef.current[k];
      clearDraft(k); // snap back to device truth
      setError(
        err?.message === "ack_timeout"
          ? "The device didn't respond — try again"
          : err?.message === "device_offline"
            ? "Device is offline"
            : `Change rejected: ${err?.message || "unknown error"}`,
      );
    },
    [clearDraft],
  );

  const sendSet = useCallback(
    (deviceId, key, value) => {
      sendSettingsSet(deviceId, key, value)
        .then((ack) => applyAck(deviceId, key, ack))
        .catch((err) => failSet(deviceId, key, err));
    },
    [applyAck, failSet],
  );

  const sendThrottled = useCallback(
    (deviceId, key, value) => {
      const k = `${deviceId}:${key}`;
      const t =
        throttleRef.current[k] ||
        (throttleRef.current[k] = { lastSentAt: 0, timer: null, pendingValue: null });
      if (t.timer) {
        t.pendingValue = value;
        return;
      }
      const elapsed = Date.now() - t.lastSentAt;
      if (elapsed >= SET_THROTTLE_MS) {
        t.lastSentAt = Date.now();
        sendSet(deviceId, key, value);
      } else {
        t.pendingValue = value;
        t.timer = setTimeout(() => {
          t.timer = null;
          const v = t.pendingValue;
          t.pendingValue = null;
          t.lastSentAt = Date.now();
          sendSet(deviceId, key, v);
        }, SET_THROTTLE_MS - elapsed);
      }
    },
    [sendSet],
  );

  const commitInput = (deviceId, setting, raw) => {
    const k = `${deviceId}:${setting.key}`;
    let value = raw;
    if (setting.type === "int") {
      value = Number(raw);
      if (!Number.isInteger(value)) {
        setError("Enter a whole number");
        return;
      }
    }
    setBusy(k, true);
    sendSettingsSet(deviceId, setting.key, value)
      .then((ack) => {
        applyAck(deviceId, setting.key, ack);
        clearDraft(k);
      })
      .catch((err) => failSet(deviceId, setting.key, err));
  };

  const renderSetting = (device, setting, disabled, showApplyHint = true) => {
    const k = `${device.id}:${setting.key}`;
    const busy = !!busyKeys[k];
    const label = settingLabel(setting.key);
    const applyHint = showApplyHint ? APPLY_HINTS[setting.apply] : null;

    let control = null;
    if (setting.type === "bool") {
      control = (
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm">{label}</span>
          <Switch
            checked={setting.value === true}
            disabled={disabled || busy}
            onCheckedChange={(v) => {
              setBusy(k, true);
              sendSet(device.id, setting.key, v === true);
            }}
          />
        </div>
      );
    } else if (setting.type === "enum" && Array.isArray(setting.options)) {
      control = (
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm">{label}</span>
          <Select
            value={String(setting.value)}
            disabled={disabled || busy}
            onValueChange={(sv) => {
              const opt = setting.options.find((o) => String(o) === sv);
              if (opt === undefined || String(opt) === String(setting.value)) return;
              setBusy(k, true);
              sendSet(device.id, setting.key, opt);
            }}
          >
            <SelectTrigger className="w-[150px] h-8 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {setting.options.map((o, i) => (
                <SelectItem key={String(o)} value={String(o)}>
                  {optionLabel(setting, o, i)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    } else if (isSliderInt(setting)) {
      const shown = drafts[k] !== undefined ? drafts[k] : setting.value;
      control = (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="text-sm">{label}</span>
            <span className="text-xs font-mono text-muted-foreground tabular-nums">
              {shown}
            </span>
          </div>
          <Slider
            value={[Number(shown) || 0]}
            min={setting.min}
            max={setting.max}
            step={1}
            disabled={disabled}
            onValueChange={([v]) => {
              delete committedRef.current[k];
              setDrafts((prev) => ({ ...prev, [k]: v }));
              sendThrottled(device.id, setting.key, v);
            }}
            onValueCommit={([v]) => {
              committedRef.current[k] = true;
              sendThrottled(device.id, setting.key, v);
            }}
          />
        </div>
      );
    } else if (setting.type === "secret") {
      const draft = drafts[k] ?? "";
      control = (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="text-sm">{label}</span>
            <Badge variant="outline" className="text-xs">
              {setting.is_set ? "Set" : "Not set"}
            </Badge>
          </div>
          <div className="flex gap-2">
            <Input
              type="password"
              autoComplete="new-password"
              value={draft}
              placeholder={setting.is_set ? "••••••••" : "Enter value"}
              disabled={disabled || busy}
              onChange={(e) =>
                setDrafts((prev) => ({ ...prev, [k]: e.target.value }))
              }
              className="h-8 text-sm"
            />
            <Button
              variant="outline"
              size="sm"
              disabled={disabled || busy || draft.length === 0}
              onClick={() => commitInput(device.id, setting, draft)}
            >
              {busy && <Loader2 className="w-3 h-3 animate-spin mr-1" />}
              Save
            </Button>
          </div>
        </div>
      );
    } else {
      // string, or an int the schema gave no slider bounds for
      const current = setting.value == null ? "" : String(setting.value);
      const draft = drafts[k] ?? current;
      const dirty = draft !== current;
      control = (
        <div className="space-y-1.5">
          <span className="text-sm">{label}</span>
          <div className="flex gap-2">
            <Input
              type={setting.type === "int" ? "number" : "text"}
              inputMode={setting.type === "int" ? "numeric" : undefined}
              value={draft}
              min={setting.type === "int" && Number.isFinite(setting.min) ? setting.min : undefined}
              max={setting.type === "int" && Number.isFinite(setting.max) ? setting.max : undefined}
              step={setting.type === "int" ? 1 : undefined}
              maxLength={setting.max_len || undefined}
              disabled={disabled || busy}
              onChange={(e) =>
                setDrafts((prev) => ({ ...prev, [k]: e.target.value }))
              }
              onKeyDown={(e) => {
                if (e.key === "Enter" && dirty) {
                  commitInput(device.id, setting, draft);
                }
              }}
              className="h-8 text-sm"
            />
            {dirty && (
              <Button
                variant="outline"
                size="sm"
                disabled={disabled || busy}
                onClick={() => commitInput(device.id, setting, draft)}
              >
                {busy && <Loader2 className="w-3 h-3 animate-spin mr-1" />}
                Save
              </Button>
            )}
          </div>
        </div>
      );
    }

    return (
      <div key={setting.key}>
        {control}
        {applyHint && (
          <p className="text-[11px] text-muted-foreground mt-0.5">{applyHint}</p>
        )}
      </div>
    );
  };

  if (!snap || snap.settings.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        {waited
          ? `${device.name} hasn't sent its settings. It sends them while it's online, on recent firmware.`
          : "Asking the timer for its settings…"}
      </p>
    );
  }

  const offline = online === false || (online === undefined && snap.cached === true);

  return (
    <div className="grid gap-5">
      {offline ? (
        <p className="rounded-xl border border-border bg-muted/20 p-3 text-xs leading-snug text-muted-foreground">
          The timer is offline, so these are its last known settings. You can
          change them when it reconnects.
        </p>
      ) : (
        <p className="text-xs text-muted-foreground">Changes apply on the timer straight away.</p>
      )}
      {error && (
        <div className="flex items-center gap-2 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}
      {groupSettings(snap.settings).map(([group, settings]) => {
        const groupHint = sectionApplyHint(settings);
        return (
          <section key={group} className="grid gap-3">
            <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              {GROUP_TITLES[group] || group}
            </h3>
            {GROUP_NOTES[group] && (
              <p className="-mt-1.5 text-xs text-muted-foreground">{GROUP_NOTES[group]}</p>
            )}
            {groupHint && (
              <p className="-mt-1.5 text-[11px] text-muted-foreground">{groupHint}</p>
            )}
            <div className="grid gap-4 rounded-xl border border-border bg-background p-3">
              {settings.map((s) => renderSetting(device, s, offline, !groupHint))}
            </div>
          </section>
        );
      })}
    </div>
  );
};

export default TimerSettingsPanel;
